// Compass azimuth: 0 = North, 90 = East, 180 = South, 270 = West.
// Roof faces have a `pitch` indicating which compass direction the face slopes
// down toward. Output multiplier per face = max(MIN, cos(pitch − sunAzimuth)).

// ────────────────────────────────────────────────────────────────────────────
// Placeable items (unified type — panels, inverters, batteries)
// ────────────────────────────────────────────────────────────────────────────

export type ItemRole = 'panel' | 'inverter' | 'battery';

export type Item = {
  id: string;
  role: ItemRole;
  name: string;
  cost: number;
  width: number;
  height: number;
  /** Panels: nameplate watts. Inverters: max kW the inverter can pass. Battery: stored kWh. */
  watts?: number;
  capacityKW?: number;
  storageKWh?: number;
  /** Battery: kW credit added to system output WHEN placed in shade (face mult ≤ 0.4). */
  bonusKW?: number;
  /** Visual */
  silicon: number;
  frame: number;
  blurb: string;
};

function p(it: Omit<Item, 'role'>): Item {
  return { ...it, role: 'panel' };
}
function inv(it: Omit<Item, 'role'>): Item {
  return { ...it, role: 'inverter' };
}
function bat(it: Omit<Item, 'role'>): Item {
  return { ...it, role: 'battery' };
}

export const PANELS: Record<string, Item> = {
  tile200: p({
    id: 'tile200',
    name: 'Tile 200W',
    watts: 200,
    cost: 110,
    width: 1,
    height: 1,
    silicon: 0x12305a,
    frame: 0xb8bcc4,
    blurb: 'Single-cell tile. Cheapest option for tight gaps and corners.',
  }),
  compact350: p({
    id: 'compact350',
    name: 'Compact 350W',
    watts: 350,
    cost: 170,
    width: 1,
    height: 2,
    silicon: 0x18386a,
    frame: 0xb8bcc4,
    blurb: 'Best $/W in the catalogue. Workhorse for budget-tight roofs.',
  }),
  std400: p({
    id: 'std400',
    name: 'Standard 400W',
    watts: 400,
    cost: 200,
    width: 1,
    height: 2,
    silicon: 0x153878,
    frame: 0xc8ccd2,
    blurb: 'Balanced everyday panel. Solid output, fair price.',
  }),
  prem500: p({
    id: 'prem500',
    name: 'Premium 500W',
    watts: 500,
    cost: 320,
    width: 1,
    height: 2,
    silicon: 0x0c2358,
    frame: 0xd8dce2,
    blurb: 'Mono-PERC, higher density. Costs more per watt — but more watts per cell.',
  }),
  bifac550: p({
    id: 'bifac550',
    name: 'Bifacial 550W',
    watts: 550,
    cost: 400,
    width: 1,
    height: 2,
    silicon: 0x0a1f4a,
    frame: 0xe6eaf0,
    blurb: 'Glass-on-glass bifacial. Premium yield in a Standard footprint.',
  }),
  mega800: p({
    id: 'mega800',
    name: 'Mega 800W',
    watts: 800,
    cost: 620,
    width: 2,
    height: 2,
    silicon: 0x081a40,
    frame: 0xe6eaf0,
    blurb: 'XL 2×2 array. Maximum watts when you have the open roof for it.',
  }),
};

export const INVERTERS: Record<string, Item> = {
  inv5k: inv({
    id: 'inv5k',
    name: '5 kW Inverter',
    capacityKW: 5,
    cost: 700,
    width: 1,
    height: 1,
    silicon: 0x252a32,
    frame: 0x9aa4b0,
    blurb: 'String inverter. Caps total system output at 5 kW. Required.',
  }),
  inv10k: inv({
    id: 'inv10k',
    name: '10 kW Inverter',
    capacityKW: 10,
    cost: 1100,
    width: 1,
    height: 2,
    silicon: 0x1a1f26,
    frame: 0xb6c0cc,
    blurb: 'Hybrid inverter. Caps system at 10 kW. Pairs with batteries.',
  }),
};

export const BATTERIES: Record<string, Item> = {
  bat5k: bat({
    id: 'bat5k',
    name: '5 kWh Battery',
    storageKWh: 5,
    bonusKW: 0.4,
    cost: 1500,
    width: 1,
    height: 1,
    silicon: 0x0e2530,
    frame: 0x39474f,
    blurb: '+0.4 kW stored credit when shaded. PENALTY in direct sun (overheat).',
  }),
  bat10k: bat({
    id: 'bat10k',
    name: '10 kWh Battery',
    storageKWh: 10,
    bonusKW: 0.8,
    cost: 2700,
    width: 2,
    height: 1,
    silicon: 0x0e2530,
    frame: 0x39474f,
    blurb: '+0.8 kW credit when shaded. Bigger storage, bigger heat penalty if sunny.',
  }),
};

export const ITEMS: Record<string, Item> = {
  ...PANELS,
  ...INVERTERS,
  ...BATTERIES,
};

export function isPanel(it: Item): boolean {
  return it.role === 'panel';
}
export function isInverter(it: Item): boolean {
  return it.role === 'inverter';
}
export function isBattery(it: Item): boolean {
  return it.role === 'battery';
}

export type Obstacle = {
  x: number;
  y: number;
  w: number;
  h: number;
  kind: 'chimney' | 'vent' | 'skylight';
};

export type RoofFace = {
  label?: string;
  pitch: number;
  cells: { x: number; y: number; w: number; h: number }[];
};


export type Level = {
  id: number;
  title: string;
  hint: string;
  faces: RoofFace[];
  obstacles: Obstacle[];
  sunAzimuthDeg: number;
  /** Power-generation target — sum of panel kW (capped by inverter, plus battery shade credit). */
  targetKW: number;
  /** Storage target — sum of battery storage kWh of placed batteries. */
  targetKWh: number;
  budget: number;
  inventory: { type: string; count: number }[];
};

// ────────────────────────────────────────────────────────────────────────────
// Levels — realistic AU residential roofs with inverters + batteries.
// Each level provides at least one inverter and one battery option.
// ────────────────────────────────────────────────────────────────────────────

export const LEVELS: Level[] = [
  {
    id: 1,
    title: '01 — North-South Gable',
    hint:
      'Simple gable: a north and a south face. Sun is south. Stack panels on the south face. Install an inverter or you get nothing.',
    faces: [
      { label: 'N pitch', pitch: 0, cells: [{ x: 0, y: 0, w: 7, h: 2 }] },
      { label: 'S pitch', pitch: 180, cells: [{ x: 0, y: 2, w: 7, h: 2 }] },
    ],
    obstacles: [],
    sunAzimuthDeg: 180,
    targetKW: 1.6,
    targetKWh: 5,
    budget: 3700,
    inventory: [
      { type: 'std400', count: 8 },
      { type: 'tile200', count: 4 },
      { type: 'inv5k', count: 1 },
      { type: 'bat5k', count: 1 },
    ],
  },
  {
    id: 2,
    title: '02 — Chimney on the Sunny Side',
    hint:
      'Same gable, but a chimney sits where you want to install. Sun is south-east. Battery in shade = bonus, in sun = penalty.',
    faces: [
      { label: 'N pitch', pitch: 0, cells: [{ x: 0, y: 0, w: 7, h: 2 }] },
      { label: 'S pitch', pitch: 180, cells: [{ x: 0, y: 2, w: 7, h: 3 }] },
    ],
    obstacles: [{ x: 3, y: 3, w: 1, h: 1, kind: 'chimney' }],
    sunAzimuthDeg: 135,
    targetKW: 2.0,
    targetKWh: 5,
    budget: 4500,
    inventory: [
      { type: 'std400', count: 8 },
      { type: 'compact350', count: 4 },
      { type: 'tile200', count: 4 },
      { type: 'inv5k', count: 1 },
      { type: 'bat5k', count: 1 },
    ],
  },
  {
    id: 3,
    title: '03 — Cross-Gable',
    hint:
      'Main gable plus a cross wing facing west. Sun is west — the wing is suddenly your best bet.',
    faces: [
      { label: 'N pitch', pitch: 0, cells: [{ x: 0, y: 0, w: 5, h: 2 }] },
      { label: 'S pitch', pitch: 180, cells: [{ x: 0, y: 2, w: 5, h: 2 }] },
      { label: 'W pitch', pitch: 270, cells: [{ x: 1, y: 4, w: 2, h: 3 }] },
      { label: 'E pitch', pitch: 90, cells: [{ x: 3, y: 4, w: 2, h: 3 }] },
    ],
    obstacles: [
      { x: 2, y: 1, w: 1, h: 1, kind: 'vent' },
      { x: 1, y: 5, w: 1, h: 1, kind: 'skylight' },
    ],
    sunAzimuthDeg: 270,
    targetKW: 2.0,
    targetKWh: 5,
    budget: 5000,
    inventory: [
      { type: 'std400', count: 5 },
      { type: 'compact350', count: 5 },
      { type: 'tile200', count: 6 },
      { type: 'inv5k', count: 1 },
      { type: 'inv10k', count: 1 },
      { type: 'bat5k', count: 1 },
    ],
  },
  {
    id: 4,
    title: '04 — Hip Roof',
    hint:
      'Four faces meeting at a central ridge. Sun is north-east. Pick the inverter to match — big systems need the 10 kW.',
    faces: [
      { label: 'N pitch', pitch: 0, cells: [{ x: 1, y: 0, w: 6, h: 2 }] },
      { label: 'E pitch', pitch: 90, cells: [{ x: 6, y: 2, w: 2, h: 3 }] },
      { label: 'S pitch', pitch: 180, cells: [{ x: 1, y: 5, w: 6, h: 2 }] },
      { label: 'W pitch', pitch: 270, cells: [{ x: 0, y: 2, w: 2, h: 3 }] },
      { label: 'Cap', pitch: 30, cells: [{ x: 2, y: 2, w: 4, h: 3 }] },
    ],
    obstacles: [
      { x: 3, y: 0, w: 1, h: 1, kind: 'vent' },
      { x: 6, y: 3, w: 1, h: 1, kind: 'chimney' },
      { x: 3, y: 6, w: 1, h: 1, kind: 'skylight' },
    ],
    sunAzimuthDeg: 45,
    targetKW: 3.5,
    targetKWh: 10,
    budget: 6500,
    inventory: [
      { type: 'std400', count: 6 },
      { type: 'prem500', count: 4 },
      { type: 'compact350', count: 6 },
      { type: 'tile200', count: 6 },
      { type: 'inv5k', count: 1 },
      { type: 'inv10k', count: 1 },
      { type: 'bat5k', count: 1 },
      { type: 'bat10k', count: 1 },
    ],
  },
  {
    id: 5,
    title: '05 — Hip-and-Valley',
    hint:
      'Suburban sprawl. Sun is north-west. Tight budget. Pair the right inverter with the right batteries — keep the batteries shaded.',
    faces: [
      { label: 'N pitch', pitch: 0, cells: [{ x: 1, y: 0, w: 5, h: 2 }] },
      { label: 'E pitch', pitch: 90, cells: [{ x: 5, y: 2, w: 2, h: 2 }] },
      { label: 'S pitch', pitch: 180, cells: [{ x: 1, y: 4, w: 5, h: 2 }] },
      { label: 'W pitch', pitch: 270, cells: [{ x: 0, y: 2, w: 1, h: 2 }] },
      { label: 'NW pitch', pitch: 315, cells: [{ x: 7, y: 0, w: 3, h: 3 }] },
      { label: 'SE pitch', pitch: 135, cells: [{ x: 7, y: 3, w: 3, h: 3 }] },
    ],
    obstacles: [
      { x: 2, y: 1, w: 1, h: 1, kind: 'chimney' },
      { x: 4, y: 0, w: 1, h: 1, kind: 'vent' },
      { x: 3, y: 5, w: 1, h: 1, kind: 'skylight' },
      { x: 8, y: 2, w: 1, h: 1, kind: 'vent' },
      { x: 7, y: 4, w: 1, h: 1, kind: 'skylight' },
    ],
    sunAzimuthDeg: 315,
    targetKW: 4.5,
    targetKWh: 15,
    budget: 8500,
    inventory: [
      { type: 'std400', count: 4 },
      { type: 'prem500', count: 4 },
      { type: 'bifac550', count: 4 },
      { type: 'mega800', count: 2 },
      { type: 'compact350', count: 4 },
      { type: 'tile200', count: 6 },
      { type: 'inv10k', count: 1 },
      { type: 'inv5k', count: 1 },
      { type: 'bat5k', count: 2 },
      { type: 'bat10k', count: 1 },
    ],
  },
];
